import {
  Table,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Stack,
  Typography,
  Checkbox,
  Button,
  Skeleton,
  styled,
  CircularProgress,
} from '@mui/material';
import React, { useContext } from 'react';
import { DoseSiteSummaryType } from './Interface';
import dayjs from 'dayjs';
import { CurrentAppConfig } from '../Careplan/AppConfig';
import { Sync } from '@mui/icons-material';
import { TenantContext } from 'op-contexts/TenantContext/TenantContext';

interface DoseSiteTableProps {
  doseSiteSummaries: DoseSiteSummaryType[];
  handleDoseSiteSummaryChange: (doseSiteSummary: DoseSiteSummaryType) => void;
  lastDoseSiteSummariesUpdatedAt: string | null;
  refreshDSS: () => void;
  loading?: boolean;
  refreshDSSLoading?: boolean;
  automaticRefreshLoad?: boolean;
  readOnly: boolean;
}

const TableCellStyled = styled(TableCell)({
  padding: '6px 12px',
  fontSize: '14px',
  letterSpacing: '0.17px',
});
// Skeleton loading component for table rows
const SkeletonTableRows = ({ rowCount = 1 }: { rowCount?: number }) => (
  <>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Stack direction="column" spacing={1} alignItems="center">
        <Skeleton variant="rectangular" width={250} height={20} />
        <Skeleton variant="rectangular" width={250} height={20} />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Skeleton variant="rectangular" width={168} height={20} />
        <Skeleton variant="rectangular" width={101} height={30} />
      </Stack>
    </Stack>
    <TableContainer component={Paper} sx={{ maxHeight: '300px' }} elevation={0}>
      <Table>
        <TableBody>
          {Array.from({ length: rowCount }, (_, index) => (
            <TableRow key={`skeleton-${index}`}>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="rectangular" width={24} height={24} />
                  <Skeleton variant="text" width="100%" height={20} />
                </Stack>
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="100%" height={20} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="100%" height={20} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="100%" height={20} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="100%" height={20} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="100%" height={20} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="100%" height={20} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

const DoseSiteTable = ({
  doseSiteSummaries,
  handleDoseSiteSummaryChange,
  lastDoseSiteSummariesUpdatedAt,
  refreshDSS,
  loading = false,
  refreshDSSLoading = false,
  automaticRefreshLoad = false,
  readOnly = false,
}: DoseSiteTableProps): JSX.Element => {
  const { doseUnit, fractionUnit } = useContext(TenantContext);
  const handleCheckboxChange = (doseSiteSummary: DoseSiteSummaryType, checked: boolean) => {
    // Avoid changing the checkbox if the table is read only
    if (readOnly) return;
    handleDoseSiteSummaryChange({ ...doseSiteSummary, isSelected: checked });
  };
  if (loading || (doseSiteSummaries.length > 0 && refreshDSSLoading) || automaticRefreshLoad) {
    return (
      <Paper sx={{ padding: 2, marginBottom: 2 }}>
        <SkeletonTableRows rowCount={3} />
      </Paper>
    );
  }
  const filterDoseSiteSummaries = doseSiteSummaries.filter(
    (doseSiteSummary: DoseSiteSummaryType) => doseSiteSummary.isSelected || !readOnly,
  );
  return (
    <Paper sx={{ padding: 2, marginBottom: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ paddingBottom: '16px' }}>
        <Stack direction="column" spacing={1}>
          <Typography variant="subtitle1">Dose Site Summary</Typography>
          {doseSiteSummaries.length > 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ paddingTop: '0px' }}>
              {readOnly
                ? 'The Dose Site Summary is locked once submitted to ensure data accuracy.'
                : 'Select the relevant DSS'}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Treatment has not started or there is no recent treatment
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Last synced:{' '}
            {lastDoseSiteSummariesUpdatedAt
              ? dayjs(lastDoseSiteSummariesUpdatedAt).format(CurrentAppConfig.OTRPage.DateTimeFormat)
              : ''}
          </Typography>
          {!readOnly && (
            <Button
              sx={{ width: '100px' }}
              variant="text"
              startIcon={refreshDSSLoading ? <CircularProgress size={20} /> : <Sync />}
              onClick={refreshDSS}
              disabled={refreshDSSLoading}>
              {refreshDSSLoading ? '      ' : 'Refresh'}
            </Button>
          )}
        </Stack>
      </Stack>
      {filterDoseSiteSummaries.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: '300px' }} elevation={0}>
          <Table stickyHeader sx={{ '& .MuiTableCell-stickyHeader': { zIndex: 1 } }}>
            <TableHead>
              <TableRow hover>
                <TableCellStyled sx={{ width: '131px' }}>Treatment Site</TableCellStyled>
                <TableCellStyled sx={{ width: '81px' }}>Course</TableCellStyled>
                <TableCellStyled sx={{ width: '106px' }}>Delivered</TableCellStyled>
                <TableCellStyled sx={{ width: '127px' }}>Prescribed</TableCellStyled>
                <TableCellStyled sx={{ width: '146px' }}>Start Tx - End Tx</TableCellStyled>
                <TableCellStyled sx={{ width: '127px' }}>Latest Tx</TableCellStyled>
                <TableCellStyled sx={{ width: '127px' }}>Technique</TableCellStyled>
              </TableRow>
            </TableHead>
            <TableBody>
              {filterDoseSiteSummaries.map((doseSiteSummary: DoseSiteSummaryType) => (
                <TableRow
                  key={doseSiteSummary.id}
                  hover
                  onClick={() => handleCheckboxChange(doseSiteSummary, !doseSiteSummary.isSelected)}>
                  <TableCellStyled sx={{ width: '131px' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {!readOnly && <Checkbox checked={doseSiteSummary?.isSelected} />}
                      {doseSiteSummary.site}
                    </Stack>
                  </TableCellStyled>
                  <TableCellStyled sx={{ width: '81px' }}>{doseSiteSummary.course}</TableCellStyled>
                  <TableCellStyled sx={{ width: '106px' }}>
                    {doseSiteSummary.deliveredDosage}
                    {doseUnit}/{doseSiteSummary.deliveredFractionDose}
                    {fractionUnit}
                  </TableCellStyled>
                  <TableCellStyled sx={{ width: '127px' }}>
                    {doseSiteSummary.prescribedDosage}
                    {doseUnit}/{doseSiteSummary.prescribedFractionDose}
                    {fractionUnit}
                  </TableCellStyled>
                  <TableCellStyled
                    sx={{ width: '146px', padding: '6px 12px', fontSize: '14px', letterSpacing: '0.17px' }}>
                    {dayjs(doseSiteSummary.treatmentStartDate).format(CurrentAppConfig.OTRPage.DateFormat)} -<br />
                    {doseSiteSummary.treatmentEndDate
                      ? dayjs(doseSiteSummary.treatmentEndDate).format(CurrentAppConfig.OTRPage.DateFormat)
                      : ''}
                  </TableCellStyled>
                  <TableCellStyled sx={{ width: '127px' }}>
                    {dayjs(doseSiteSummary.treatmentLatestDate).format(CurrentAppConfig.OTRPage.DateFormat)}
                  </TableCellStyled>
                  <TableCellStyled sx={{ width: '127px' }}>{doseSiteSummary.technique}</TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default DoseSiteTable;
